/**
 * Image Processing Utilities
 *
 * Handles client-side image resizing and optimization before upload.
 */

import { UPLOAD_CONFIG } from '../config/upload';

/**
 * Checks if an image needs to be resized based on configured max dimension
 *
 * @param {number} width - Image width in pixels
 * @param {number} height - Image height in pixels
 * @returns {boolean} - True if image needs resizing
 */
export const needsResize = (width, height) => {
  return width > UPLOAD_CONFIG.MAX_DIMENSION || height > UPLOAD_CONFIG.MAX_DIMENSION;
};

/**
 * Calculates new dimensions while maintaining aspect ratio
 *
 * @param {number} width - Original width
 * @param {number} height - Original height
 * @param {number} maxDimension - Maximum allowed dimension
 * @returns {{width: number, height: number}} - New dimensions
 */
export const calculateResizedDimensions = (width, height, maxDimension = UPLOAD_CONFIG.MAX_DIMENSION) => {
  if (!needsResize(width, height)) {
    return { width, height };
  }

  const aspectRatio = width / height;

  if (width > height) {
    // Landscape or square
    return {
      width: maxDimension,
      height: Math.round(maxDimension / aspectRatio)
    };
  } else {
    // Portrait
    return {
      width: Math.round(maxDimension * aspectRatio),
      height: maxDimension
    };
  }
};

/**
 * Resizes an image file to fit within max dimensions while maintaining aspect ratio
 *
 * @param {File} file - The image file to resize
 * @param {number} maxDimension - Maximum width or height (defaults to config)
 * @returns {Promise<File>} - Resized image file (or original if no resize needed)
 */
export const resizeImage = async (file, maxDimension = UPLOAD_CONFIG.MAX_DIMENSION) => {
  return new Promise((resolve, reject) => {
    // Create an image element to load the file
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      try {
        const originalWidth = img.width;
        const originalHeight = img.height;

        console.log(`[ImageProcessing] Original dimensions: ${originalWidth}x${originalHeight}`);

        // Check if resize is needed
        if (!needsResize(originalWidth, originalHeight)) {
          console.log(`[ImageProcessing] Image is already within ${maxDimension}px limit, no resize needed`);
          resolve(file);
          return;
        }

        // Calculate new dimensions
        const { width: newWidth, height: newHeight } = calculateResizedDimensions(
          originalWidth,
          originalHeight,
          maxDimension
        );

        console.log(`[ImageProcessing] Resizing to: ${newWidth}x${newHeight} (max: ${maxDimension}px)`);

        // Set canvas dimensions to new size
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Draw resized image on canvas
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Determine output format
        let outputFormat = file.type;
        if (UPLOAD_CONFIG.OUTPUT_FORMAT !== 'original') {
          outputFormat = UPLOAD_CONFIG.OUTPUT_FORMAT;
        }

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob from canvas'));
              return;
            }

            // Create new File object from blob
            const resizedFile = new File([blob], file.name, {
              type: outputFormat,
              lastModified: Date.now()
            });

            const originalSizeKB = (file.size / 1024).toFixed(2);
            const newSizeKB = (resizedFile.size / 1024).toFixed(2);
            const savings = (((file.size - resizedFile.size) / file.size) * 100).toFixed(1);

            console.log(`[ImageProcessing] Resize complete:`);
            console.log(`  - Original: ${originalWidth}x${originalHeight} (${originalSizeKB} KB)`);
            console.log(`  - Resized: ${newWidth}x${newHeight} (${newSizeKB} KB)`);
            console.log(`  - Savings: ${savings}%`);

            resolve(resizedFile);
          },
          outputFormat,
          UPLOAD_CONFIG.RESIZE_QUALITY
        );
      } catch (error) {
        console.error('[ImageProcessing] Error during resize:', error);
        reject(error);
      }
    };

    img.onerror = (error) => {
      console.error('[ImageProcessing] Failed to load image:', error);
      reject(new Error('Failed to load image for resizing'));
    };

    // Load the image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = (error) => {
      console.error('[ImageProcessing] Failed to read file:', error);
      reject(new Error('Failed to read file for resizing'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Processes an image file before upload (resizing if needed)
 *
 * This is the main function to call before uploading an image.
 *
 * @param {File} file - The image file to process
 * @returns {Promise<File>} - Processed image file
 */
export const processImageForUpload = async (file) => {
  console.log(`[ImageProcessing] Processing image for upload: ${file.name}`);

  try {
    const processedFile = await resizeImage(file);
    console.log(`[ImageProcessing] Image processing complete`);
    return processedFile;
  } catch (error) {
    console.error('[ImageProcessing] Image processing failed, using original file:', error);
    // Fallback to original file if processing fails
    return file;
  }
};
